<template>
  <!-- No free seats on the plan — offer to contact support to add more. -->
  <div v-if="!canAdd" class="flex flex-col items-center text-center w-full py-4">
    <div class="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
      <i class="pi pi-users text-xl" />
    </div>
    <div class="text-base font-semibold mb-1.5">אין מקומות פנויים בחבילה</div>
    <p class="text-[13px] text-gray-500 leading-relaxed mb-5 max-w-[280px]">
      ניצלתם את כל מקומות העובדים בחבילה שלכם. דברו איתנו ונשמח להוסיף לכם מקומות נוספים.
    </p>
    <button
      type="button"
      class="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white text-sm font-medium bg-gradient-to-br from-[#8864ee] to-[#6831d0]"
      @click="contactSupport"
    >
      <i class="pi pi-whatsapp" /> דברו איתנו בוואטסאפ
    </button>
  </div>

  <div v-else class="flex flex-col w-full">
    <p class="text-xs text-gray-500 leading-relaxed mb-4">
      קודם הפרטים הבסיסיים. מיד לאחר מכן נשייך שירותים ושעות פעילות — עד אז העובד/ת ימתין/תמתין
      ברשימת "ממתינים להשלמה".
    </p>

    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-1.5">
        <label class="text-xs text-gray-500">שם מלא</label>
        <InputText v-model="fullName" placeholder="שם ושם משפחה" class="w-full" />
      </div>

      <div class="flex gap-2">
        <div class="flex flex-col gap-1.5 flex-1">
          <label class="text-xs text-gray-500">טלפון</label>
          <InputText v-model="phone" placeholder="05X-XXXXXXX" class="w-full" style="direction: ltr; text-align: right" />
        </div>
        <div class="flex flex-col gap-1.5 flex-1">
          <label class="text-xs text-gray-500">אימייל</label>
          <InputText v-model="email" placeholder="name@mail.com" class="w-full" style="direction: ltr; text-align: right" />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-xs text-gray-500">סיסמה ראשונית</label>
        <InputText
          v-model="password"
          type="password"
          placeholder="לפחות 8 תווים, אות וספרה"
          class="w-full"
          style="direction: ltr; text-align: right"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-xs text-gray-500">תפקיד</label>
        <div class="flex gap-1.5">
          <button
            v-for="opt in roleOptions"
            :key="opt.value"
            type="button"
            class="flex-1 text-center py-2.5 rounded-lg text-sm border transition-colors"
            :class="role === opt.value
              ? 'bg-primary-50 text-primary-700 border-primary-300 font-medium'
              : 'bg-white text-gray-600 border-gray-200'"
            @click="role = opt.value"
          >{{ opt.label }}</button>
        </div>
      </div>
    </div>

    <Button
      type="button"
      class="w-full shadow-xl md:shadow-none my-4"
      :disabled="!canSubmit || loading"
      @click="submit"
    >
      <i v-if="loading" class="pi pi-spin pi-spinner text-sm" />
      יצירה והמשך לשיוך
    </Button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { ClientRoleEnum } from '@torim-app/types';
import { sendWhatsApp } from '@torim-app/utils';
import InputText from 'primevue/inputtext';
import { useToast } from 'vue-toastification';
import { SettingsApi } from '@app/api/admin/settings-api';
import { trackEvent } from '@app/composables/posthog';

withDefaults(defineProps<{ canAdd?: boolean }>(), { canAdd: true });
const emit = defineEmits<{ done: [userId: string]; close: [] }>();

const toast = useToast();

const contactSupport = () => {
  trackEvent('team_add_employee_no_seats_contact');
  sendWhatsApp('972559299380', 'היי, אשמח להוסיף מקומות עובדים לחבילה שלי');
};

const fullName = ref('');
const phone = ref('');
const email = ref('');
const password = ref('');
const role = ref<ClientRoleEnum>(ClientRoleEnum.Employee);
const loading = ref(false);

const roleOptions: Array<{ label: string; value: ClientRoleEnum }> = [
  { label: 'עובד/ת', value: ClientRoleEnum.Employee },
  { label: 'מנהל/ת', value: ClientRoleEnum.Operator },
  { label: 'צפייה בלבד', value: ClientRoleEnum.ReadOnly },
];

// SuperTokens' default password policy: 8+ chars with at least one letter and one digit.
const isPasswordValid = computed(() =>
  password.value.length >= 8 && /[a-zA-Z]/.test(password.value) && /\d/.test(password.value)
);
const isEmailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()));
const canSubmit = computed(() => fullName.value.trim().length > 0 && isEmailValid.value && isPasswordValid.value);

const submit = async () => {
  if (!canSubmit.value || loading.value) return;
  loading.value = true;
  trackEvent('team_employee_created');
  try {
    const { userId } = await SettingsApi.createEmployee({
      email: email.value.trim(),
      fullName: fullName.value.trim(),
      phoneNumber: phone.value.trim() || undefined,
      role: role.value,
      password: password.value,
    });
    toast.success('העובד/ת נוצר/ה בהצלחה 😄');
    emit('done', userId);
  } catch (e: any) {
    const message = e?.message === 'Client with this email already exists!'
      ? 'כתובת המייל כבר קיימת במערכת'
      : e?.message === 'Employee limit reached for this plan'
        ? 'הגעתם למכסת העובדים בחבילה'
        : 'לא הצלחנו ליצור את העובד/ת';
    toast.error(message);
  } finally {
    loading.value = false;
  }
};
</script>
